import clsx from 'clsx';
import { ChevronDown, ChevronUp, Copy, Plus, Trash2 } from 'lucide-react';
import { sceneAt, songBeats } from '../audio/sequencer';
import { useBeat } from '../lib/hooks';
import { useStore } from '../store/useStore';
import { NumberField, Select } from './ui/Controls';

/** Sentinels for the two non-pattern clip states in the dropdown. */
const FOLLOW = '__follow';
const SILENT = '__silent';

/**
 * Arrangement grid: scenes down the page, tracks across. Each cell picks which
 * pattern that track plays for the length of the scene, so a full song is
 * "which chop, where" rather than a giant timeline of audio.
 */
export function SongView() {
  const project = useStore((s) => s.project);
  const store = useStore();
  const beat = useBeat();
  const playing = useStore((s) => s.playing);
  const playMode = useStore((s) => s.playMode);

  const totalBeats = songBeats(project);
  const seconds = (totalBeats * 60) / project.bpm;
  const activeScene =
    playing && playMode === 'song' ? sceneAt(project, beat % Math.max(1, totalBeats))?.index : -1;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-auto p-4">
      <div className="flex items-center gap-3">
        <span className="label">Arrangement</span>
        <span className="mono text-[11px] text-ink-400">
          {project.scenes.reduce((a, s) => a + s.bars, 0)} bars · {Math.floor(seconds / 60)}:
          {String(Math.floor(seconds % 60)).padStart(2, '0')}
        </span>
        <button className="btn ml-auto" onClick={() => store.addScene()}>
          <Plus size={12} /> Add scene
        </button>
      </div>

      <table className="w-full border-separate border-spacing-1 text-[11px]">
        <thead>
          <tr>
            <th className="label w-40 text-left">Scene</th>
            <th className="label w-20 text-left">Bars</th>
            {project.tracks.map((t) => (
              <th key={t.id} className="label text-left" style={{ color: t.color }}>
                {t.name}
              </th>
            ))}
            <th className="w-24" />
          </tr>
        </thead>
        <tbody>
          {project.scenes.map((scene, i) => (
            <tr
              key={scene.id}
              className={clsx(activeScene === i && 'relative')}
            >
              <td>
                <input
                  className={clsx(
                    'field w-full',
                    activeScene === i && 'border-volt-500 text-volt-400',
                  )}
                  value={scene.name}
                  onChange={(e) => store.updateScene(scene.id, { name: e.target.value })}
                />
              </td>
              <td>
                <NumberField
                  value={scene.bars}
                  min={1}
                  max={64}
                  width="w-16"
                  onChange={(v) => store.updateScene(scene.id, { bars: Math.max(1, Math.round(v)) })}
                />
              </td>
              {project.tracks.map((t) => {
                const set = t.id in scene.clips;
                const patternId = set ? scene.clips[t.id]?.patternId ?? SILENT : FOLLOW;
                const following = patternId === FOLLOW;
                const sounding = following ? !!t.patternId : patternId !== SILENT;
                return (
                  <td key={t.id}>
                    <Select
                      className="w-full !text-[10px]"
                      style={
                        sounding
                          ? { borderLeft: `3px solid ${t.color}` }
                          : { color: 'var(--color-ink-400)' }
                      }
                      value={patternId}
                      options={[
                        { value: FOLLOW, label: '↳ follow track' },
                        { value: SILENT, label: '— silent —' },
                        ...project.patterns.map((p) => ({ value: p.id, label: p.name })),
                      ]}
                      onChange={(v) =>
                        store.setClip(
                          scene.id,
                          t.id,
                          v === FOLLOW ? undefined : v === SILENT ? null : v,
                        )
                      }
                    />
                  </td>
                );
              })}
              <td>
                <div className="flex gap-0.5">
                  <button
                    className="btn btn-icon"
                    title="Move up"
                    onClick={() => store.moveScene(i, i - 1)}
                  >
                    <ChevronUp size={11} />
                  </button>
                  <button
                    className="btn btn-icon"
                    title="Move down"
                    onClick={() => store.moveScene(i, i + 1)}
                  >
                    <ChevronDown size={11} />
                  </button>
                  <button
                    className="btn btn-icon"
                    title="Duplicate scene"
                    onClick={() => store.duplicateScene(scene.id)}
                  >
                    <Copy size={11} />
                  </button>
                  <button
                    className="btn btn-icon"
                    title="Delete scene"
                    onClick={() => store.removeScene(scene.id)}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-2">
        <div className="label mb-1">Timeline</div>
        <div className="flex h-8 gap-0.5 overflow-hidden rounded-lg border border-ink-700 bg-ink-900">
          {project.scenes.map((scene, i) => (
            <div
              key={scene.id}
              className={clsx(
                'flex items-center justify-center text-[10px] font-medium transition-colors',
                activeScene === i ? 'bg-volt-500/30 text-volt-400' : 'bg-ink-800 text-ink-400',
              )}
              style={{ flex: scene.bars }}
              title={`${scene.name} · ${scene.bars} bars`}
            >
              {scene.name}
            </div>
          ))}
        </div>
      </div>

      <p className="mt-1 text-[11px] leading-relaxed text-ink-500">
        Scenes play top to bottom. Hit <span className="text-ink-300">Song</span> in the transport to
        audition the whole arrangement, or <span className="text-ink-300">Pattern</span> to keep
        looping what you're editing.
      </p>
    </div>
  );
}
